import type { Instance } from "flatpickr/dist/types/instance";

import "flatpickr/dist/plugins/monthSelect/style.css";
import type { Options } from "flatpickr/dist/types/options";
import type { FocusEvent } from "react";

import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { InternalDatePickerProps } from "./types";

import { DateTimePickerMarkup } from "./markup";
import { tryParseDate, tryParseTime } from "./parse";

export { tryParseTime, tryParseDate };

export const DateTimePicker = memo((props: InternalDatePickerProps) => {
	const {
		format,
		enableTime,
		enableDate,
		dateOnly,
		minDate,
		maxDate,
		minTime,
		maxTime,
		minuteIncrement,
		allowInput = true,
		enabledDates,
		showMonths,
		monthsOnly,
		...rest
	} = props;
	const instanceRef = useRef<Instance>();
	const [localValue, setLocalValue] = useState<Date | string | number>(props.value);
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		if (!isFocused) {
			setLocalValue(props.value);
		}
	}, [props.value, isFocused]);

	/**
	 * On change handler. Will call the change handler that is provided,
	 * and if the onChange is not undefined, will also call that
	 * @param selectedDates: Date selected
	 * @param dateStr: Stringified date selected
	 * @param instance: Instance of the date time picker
	 */
	const change = useCallback(
		function (selectedDates: Date[], dateStr: string, instance: Instance) {
			if (!selectedDates?.length) selectedDates = [];
			// If allowInput is true, and the input is currently focused, it's a manual entry.
			// We want to wait until blur to handle manual entries to prevent premature parsing.
			if (allowInput && instance?.input === document.activeElement) {
				setLocalValue(instance.input.value);
				return;
			}
			const newValue = selectedDates.length > 0 ? selectedDates[0] : undefined;

			setLocalValue(newValue);
			if (props.changeHandler !== undefined) props.changeHandler(newValue, props.id);
			if (props.onChange !== undefined) {
				props.onChange(selectedDates, dateStr, instance);
			}
		},
		[allowInput, props],
	);

	const changeOnBlur = useCallback(
		function (selectedDates: Date[], dateStr: string, instance: Instance) {
			if (!selectedDates?.length) selectedDates = [];
			const newValue = selectedDates.length > 0 ? selectedDates[0] : undefined;
			setLocalValue(newValue);
			if (props.changeHandler !== undefined) props.changeHandler(newValue, props.id);
			if (props.onChange !== undefined) {
				props.onChange(selectedDates, dateStr, instance);
			}
		},
		[props],
	);

	const parseDateLocal = useCallback(
		(dateString: string | Date) => tryParseDate(dateString, { enableDate, enableTime, dateOnly }),
		[enableDate, enableTime, dateOnly],
	);

	const handleManualInput = useCallback(
		(rawInput?: string) => {
			setIsFocused(false);

			const raw =
				rawInput ||
				instanceRef.current?.altInput?.value ||
				instanceRef.current?.input?.value ||
				(typeof localValue === "string" ? localValue : "");

			if (raw === "" || monthsOnly) return;
			if (typeof props.value === "number") return;

			let date = parseDateLocal(raw);
			const currentValue = props.value;
			const current = currentValue ? parseDateLocal(currentValue) : undefined;

			// If a date wasn't parsed, attempt time-only parse and apply to existing/current date
			if (!date) {
				const parsedTime = tryParseTime(raw);
				if (parsedTime && enableTime !== false && !dateOnly) {
					const base = current instanceof Date ? new Date(current) : new Date();
					base.setHours(
						parsedTime.hours,
						parsedTime.minutes ?? 0,
						parsedTime.seconds ?? 0,
						parsedTime.ms ?? 0,
					);
					date = base;
				} else {
					return;
				}
			}

			// Preserve or apply time components appropriately on blur
			if (enableTime !== false && !dateOnly && current instanceof Date) {
				const hasTimeInInput = tryParseTime(raw) !== null;
				if (enableDate === false) {
					// Time-only mode: keep the existing date, apply the newly parsed time
					const merged = new Date(current);
					merged.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
					date = merged;
				} else if (!hasTimeInInput) {
					// User edited only the date: preserve the existing time
					const merged = new Date(date);
					merged.setHours(
						current.getHours(),
						current.getMinutes(),
						current.getSeconds(),
						current.getMilliseconds(),
					);
					date = merged;
				}
			}

			if (date && current && date.getTime() === current.getTime()) {
				// If the date is the same, but the local value is still a string,
				// we should still update localValue to the date object to ensure consistency
				if (typeof localValue === "string") {
					setLocalValue(date);
				}
				return;
			}

			changeOnBlur([date], raw, instanceRef.current);
		},
		[parseDateLocal, props.value, enableTime, enableDate, dateOnly, changeOnBlur, monthsOnly, localValue],
	);

	const blur = useCallback(
		(event: FocusEvent<HTMLInputElement>) => {
			handleManualInput(event.target.value);
		},
		[handleManualInput],
	);

	useEffect(() => {
		return () => {
			if (instanceRef.current) {
				instanceRef.current.destroy();
				instanceRef.current = undefined;
			}
		};
	}, []);

	const dateFormat = useMemo(() => {
		const formatObj = new Intl.DateTimeFormat(window.navigator.language).formatToParts(new Date());
		return formatObj
			.map((obj) => {
				switch (obj.type) {
					case "day":
						return obj.value.length < 2 ? "j" : "d";
					case "month":
						return obj.value.length < 2 ? "n" : "m";
					case "year":
						return obj.value.length < 2 ? "y" : "Y";
					default:
						return obj.value;
				}
			})
			.join(``);
	}, []);

	const options = useMemo(() => {
		const datePickerOptions: Options = {
			dateFormat: `${dateFormat} h:i K`,
			altFormat: `${dateFormat} h:i K`,
			altInput: true,
			enableTime: true,
			//MSC 1/19/24 - For whatever reason, mobile view won't undisable if it starts disabled.  So we have to drop the mobile view
			disableMobile: true,

			parseDate: parseDateLocal,
			static: true,
			onClose: (_selectedDates: Date[], _dateStr: string, _instance: Instance) => {
				handleManualInput();
			},
		};
		if (dateOnly || enableTime === false) {
			datePickerOptions.dateFormat = dateFormat;
			datePickerOptions.altFormat = dateFormat;
			datePickerOptions.altInput = true;
			datePickerOptions.enableTime = false;
		}
		if (format !== undefined) {
			datePickerOptions.dateFormat = format;
		}
		if (enableDate !== undefined) {
			datePickerOptions.noCalendar = !enableDate;
		}
		if (minTime !== undefined) {
			datePickerOptions.minTime = minTime;
		}
		if (maxTime !== undefined) {
			datePickerOptions.maxTime = maxTime;
		}
		if (minDate !== undefined) {
			datePickerOptions.minDate = minDate;
		}
		if (maxDate !== undefined) {
			datePickerOptions.maxDate = maxDate;
		}
		if (minuteIncrement !== undefined) {
			datePickerOptions.minuteIncrement = minuteIncrement;
		}
		if (allowInput !== undefined) {
			datePickerOptions.allowInput = allowInput;
		}
		if (enabledDates !== undefined) {
			datePickerOptions.enable = enabledDates;
		}
		if (showMonths !== undefined) {
			datePickerOptions.showMonths = showMonths;
		}
		if (monthsOnly === true) {
			datePickerOptions.altFormat = "F Y";
			datePickerOptions.altInput = true;
			datePickerOptions.plugins = [monthSelectPlugin({ shorthand: true })];
		}

		return datePickerOptions;
	}, [
		dateFormat,
		parseDateLocal,
		dateOnly,
		enableTime,
		format,
		enableDate,
		minTime,
		maxTime,
		minDate,
		maxDate,
		minuteIncrement,
		allowInput,
		enabledDates,
		showMonths,
		monthsOnly,
		handleManualInput,
	]);

	// The date is stored as a string sometimes, so we need to convert it back to a Date object
	const value = useMemo(() => {
		if (localValue === undefined || localValue === null || localValue === "") return "";
		let date: Date;
		if (typeof localValue === "string") {
			// If it's a string from manual input while focused, return it as-is to allow typing
			if (isFocused) return localValue;
			date = new Date(localValue);
		} else if (typeof localValue === "number") {
			date = new Date(localValue);
		} else {
			date = localValue;
		}
		return date;
	}, [localValue, isFocused]);

	const onFocus = useCallback(() => {
		setIsFocused(true);
	}, []);

	const onReady = useCallback((_selectedDates: Date[], _dateStr: string, instance: Instance) => {
		instanceRef.current = instance;
	}, []);

	return (
		<DateTimePickerMarkup
			{...rest}
			value={value}
			onChange={change}
			onFocus={onFocus}
			onUnfocused={blur}
			onReady={onReady}
			datePickerOptions={options}
		/>
	);
});

export * from "./types";
